import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { INCLUSION_FREQUENCIES } from '@hypo/shared'
import { DetailEditor } from './detail-editor.js'

// --- Styles ---

const sectionTitle = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.semibold};
  color: ${colors.textSecondary};
  margin-bottom: ${space.sm};
  margin-top: ${space.lg};
  &:first-child { margin-top: 0; }
`

const omissionRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const omissionList = css`
  border: 1px solid ${colors.border};
  border-radius: 6px;
  overflow: hidden;
`

const omissionName = css`
  flex: 1;
  font-size: ${font.size.sm};
`

const omissionSelect = css`
  ${input};
  width: auto;
  font-size: ${font.size.sm};
  padding: ${space.xs} ${space.sm};
  appearance: auto;
`

const chipWrap = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  cursor: pointer;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  user-select: none;
  transition: background 0.1s, border-color 0.1s;
  &:hover { border-color: ${colors.brand}; }
`

const snippetRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const snippetList = css`
  border: 1px solid ${colors.border};
  border-radius: 6px;
  overflow: hidden;
`

const snippetContent = css`
  flex: 1;
  font-size: ${font.size.sm};
  white-space: pre-wrap;
`

const snippetActions = css`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`

const smallBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.sm};
`

const addBtnStyle = css`
  ${btnSecondary};
  font-size: ${font.size.sm};
  margin-top: ${space.sm};
`

const inlineForm = css`
  display: flex;
  gap: ${space.sm};
  margin-top: ${space.sm};
`

const emptyText = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  padding: ${space.md};
  text-align: center;
`

const detailEditorWrap = css`
  padding: ${space.md};
  border-bottom: 1px solid ${colors.border};
  background: ${colors.bgSidebar};
`

/**
 * NoiseEditor component
 *
 * attrs:
 *   relevantDetails: [{ id, name, type, omission_freq }]
 *   allDetails: all pack details
 *   irrelevantDetails: [{detail_id, inclusion_freq}]
 *   noiseSnippets: [{ id, content }]
 *   onSetOmissionFreq: (detailId, freq) => Promise
 *   onSetIrrelevantDetails: (detailIds) => Promise
 *   onSetInclusionFreq: (detailId, freq) => Promise
 *   onCreateIrrelevantDetail: (data) => Promise
 *   onUpdateIrrelevantDetail: (detailId, data) => Promise
 *   onCreateSnippet: (data) => Promise
 *   onUpdateSnippet: (snippetId, data) => Promise
 *   onDeleteSnippet: (snippetId) => Promise
 *   saving: boolean
 */
export function NoiseEditor() {
  let showAddSnippet = false
  let newSnippetContent = ''
  let editingSnippetId = null
  let editingSnippetContent = ''
  let showAddDetail = false
  let editingDetailId = null

  return {
    view(vnode) {
      const {
        relevantDetails = [],
        allDetails = [],
        irrelevantDetails = [],
        noiseSnippets = [],
        onSetOmissionFreq,
        onSetIrrelevantDetails,
        onSetInclusionFreq,
        onCreateIrrelevantDetail,
        onUpdateIrrelevantDetail,
        onCreateSnippet,
        onUpdateSnippet,
        onDeleteSnippet,
        saving,
      } = vnode.attrs

      // Build sets for filtering
      const relevantIdSet = new Set(relevantDetails.map(d => d.id))
      const irrelevantIdSet = new Set(irrelevantDetails.map(d => d.detail_id))
      // Build inclusion_freq lookup
      const inclusionFreqMap = Object.fromEntries(irrelevantDetails.map(d => [d.detail_id, d.inclusion_freq]))
      // Available to add = not relevant AND not already irrelevant
      const availableToAdd = allDetails.filter(d => !relevantIdSet.has(d.id) && !irrelevantIdSet.has(d.id))
      // Selected irrelevant details with full detail data
      const selectedIrrelevant = allDetails.filter(d => irrelevantIdSet.has(d.id))

      const children = []

      // --- Section 1: Inclusion Frequency ---
      children.push(m('h3', { key: 'title-inclusion', class: sectionTitle }, 'Inclusion Frequency'))
      children.push(m('p', { key: 'desc-inclusion', style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; margin-bottom: ${space.sm}; margin-top: 0;` }, 'How often does the user include each detail?'))

      if (relevantDetails.length > 0) {
        children.push(m('div', { key: 'omission-list', class: omissionList },
          relevantDetails.map(d =>
            m('div', { key: d.id, class: omissionRow }, [
              m('span', { class: omissionName }, d.name),
              m('select', {
                class: omissionSelect,
                value: d.omission_freq || 'sometimes',
                disabled: saving,
                onchange: (e) => onSetOmissionFreq(d.id, e.target.value),
              }, INCLUSION_FREQUENCIES.map(freq =>
                m('option', { key: freq, value: freq }, freq)
              )),
            ])
          )
        ))
      } else {
        children.push(m('div', { key: 'omission-empty', class: emptyText }, 'No relevant details selected. Go back to Step 2 to add details.'))
      }

      // --- Section 2: Irrelevant Details ---
      children.push(m('h3', { key: 'title-irrelevant', class: sectionTitle }, 'Irrelevant Details'))

      if (selectedIrrelevant.length > 0) {
        const listChildren = []
        for (const d of selectedIrrelevant) {
          const isEditing = editingDetailId === d.id
          listChildren.push(m('div', { key: `row-${d.id}`, class: omissionRow }, [
            m('span', { class: omissionName }, d.name),
            m('select', {
              class: omissionSelect,
              value: inclusionFreqMap[d.id] || 'sometimes',
              disabled: saving,
              onchange: (e) => onSetInclusionFreq(d.id, e.target.value),
            }, INCLUSION_FREQUENCIES.map(freq =>
              m('option', { key: freq, value: freq }, freq)
            )),
            m('button', {
              class: smallBtn,
              onclick: () => { editingDetailId = isEditing ? null : d.id },
            }, isEditing ? 'Cancel' : 'Edit'),
            m('button', {
              class: smallBtn,
              disabled: saving,
              onclick: () => {
                const newIds = irrelevantDetails
                  .filter(entry => entry.detail_id !== d.id)
                  .map(entry => entry.detail_id)
                onSetIrrelevantDetails(newIds)
              },
            }, '\u00d7'),
          ]))
          if (isEditing) {
            listChildren.push(m('div', { key: `edit-${d.id}`, class: detailEditorWrap }, [
              m(DetailEditor, {
                detail: d,
                onSave: async (data) => {
                  await onUpdateIrrelevantDetail(d.id, data)
                  editingDetailId = null
                },
                onCancel: () => { editingDetailId = null },
              }),
            ]))
          }
        }
        children.push(m('div', { key: 'irrelevant-list', class: omissionList }, listChildren))
      } else {
        children.push(m('div', { key: 'irrelevant-empty', class: emptyText }, 'No irrelevant details added yet.'))
      }

      // Available details to add as chips
      if (availableToAdd.length > 0) {
        children.push(m('div', { key: 'add-chips', class: chipWrap, style: `margin-top: ${space.sm};` },
          availableToAdd.map(d =>
            m('span', {
              key: d.id,
              class: chip,
              onclick: () => {
                const newIds = [...irrelevantDetails.map(entry => entry.detail_id), d.id]
                onSetIrrelevantDetails(newIds)
              },
            }, ['+ ', d.name])
          )
        ))
      }

      children.push(m('button', {
        key: 'add-irrelevant-btn',
        class: addBtnStyle,
        onclick: () => { showAddDetail = !showAddDetail },
      }, showAddDetail ? 'Cancel' : '+ Create New Detail'))

      if (showAddDetail) {
        children.push(m('div', {
          key: 'add-irrelevant-form',
          class: css`
            margin-top: ${space.md};
            padding: ${space.md};
            border: 1px solid ${colors.border};
            border-radius: 6px;
            background: ${colors.bgCard};
          `,
        }, [
          m(DetailEditor, {
            detail: null,
            onSave: async (data) => {
              await onCreateIrrelevantDetail(data)
              showAddDetail = false
            },
            onCancel: () => { showAddDetail = false },
          }),
        ]))
      }

      // --- Section 3: Descriptive Noise Snippets ---
      children.push(m('h3', { key: 'title-snippets', class: sectionTitle }, 'Descriptive Noise Snippets'))

      if (noiseSnippets.length > 0) {
        children.push(m('div', { key: 'snippet-list', class: snippetList },
          noiseSnippets.map(s => {
            const isEditing = editingSnippetId === s.id
            if (isEditing) {
              return m('div', { key: s.id, class: snippetRow, style: 'flex-direction: column; align-items: stretch;' }, [
                m('textarea', {
                  class: input,
                  rows: 3,
                  value: editingSnippetContent,
                  oninput: (e) => { editingSnippetContent = e.target.value },
                }),
                m('div', { style: `display: flex; gap: ${space.sm}; justify-content: flex-end; margin-top: ${space.xs};` }, [
                  m('button', {
                    class: smallBtn,
                    onclick: () => { editingSnippetId = null },
                  }, 'Cancel'),
                  m('button', {
                    class: css`${btnPrimary}; font-size: ${font.size.xs}; padding: ${space.xs} ${space.sm};`,
                    disabled: saving || !editingSnippetContent.trim(),
                    onclick: async () => {
                      await onUpdateSnippet(s.id, { content: editingSnippetContent.trim() })
                      editingSnippetId = null
                    },
                  }, 'Save'),
                ]),
              ])
            }
            return m('div', { key: s.id, class: snippetRow }, [
              m('span', { class: snippetContent }, s.content),
              m('div', { class: snippetActions }, [
                m('button', {
                  class: smallBtn,
                  onclick: () => {
                    editingSnippetId = s.id
                    editingSnippetContent = s.content
                  },
                }, 'Edit'),
                m('button', {
                  class: smallBtn,
                  disabled: saving,
                  onclick: () => onDeleteSnippet(s.id),
                }, 'Delete'),
              ]),
            ])
          })
        ))
      } else {
        children.push(m('div', { key: 'snippet-empty', class: emptyText }, 'No noise snippets yet. Add some below.'))
      }

      children.push(m('button', {
        key: 'add-snippet-btn',
        class: addBtnStyle,
        onclick: () => { showAddSnippet = !showAddSnippet },
      }, showAddSnippet ? 'Cancel' : '+ Add Snippet'))

      if (showAddSnippet) {
        children.push(m('div', { key: 'add-snippet-form', class: inlineForm }, [
          m('textarea', {
            class: css`${input}; flex: 1;`,
            rows: 2,
            value: newSnippetContent,
            oninput: (e) => { newSnippetContent = e.target.value },
            placeholder: 'Add a noise snippet...',
          }),
          m('button', {
            class: css`${btnPrimary}; align-self: flex-end;`,
            disabled: saving || !newSnippetContent.trim(),
            onclick: async () => {
              await onCreateSnippet({ content: newSnippetContent.trim() })
              newSnippetContent = ''
              showAddSnippet = false
            },
          }, 'Add'),
        ]))
      }

      return m('div', children)
    },
  }
}
